import { type SyntheticEvent, useEffect, useRef, useState } from 'react';
import { CHANGELOG } from '../changelog.js';

/** Ostatnia wersja listy zmian odhaczona przez gracza jako przeczytana. */
const STORAGE_KEY = 'sw_whats_new_seen';
const CONTACT_EMAIL = 'seawars@pi24.pl';

const LATEST_ID = CHANGELOG[0]?.id ?? null;

function readSeen(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // Prywatne okno albo zablokowane dane witryny — okno pokaże się przy każdym starcie.
    return null;
  }
}

function writeSeen(id: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* jw. — wygoda, nie dane krytyczne */
  }
}

/** `RRRR-MM-DD` jako data lokalna — `new Date('2026-09-24')` to północ UTC, dzień wcześniej w części stref. */
function formatDate(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  if (!year || !month || !day) return iso;
  return new Date(year, month - 1, day).toLocaleDateString('pl-PL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * „Co nowego” przy starcie strony/aplikacji: informacja o wersji beta i lista ostatnich zmian
 * z datami wdrożeń (`changelog.ts`). Pokazuje się przy każdym załadowaniu, dopóki gracz nie
 * zaznaczy „nie pokazuj do następnych zmian” — wtedy wraca dopiero z nowym wpisem.
 * Zapamiętanie w `localStorage`: to wygoda jednego urządzenia, nie stan konta.
 */
export function WhatsNewDialog() {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [open, setOpen] = useState(() => LATEST_ID !== null && readSeen() !== LATEST_ID);
  const [dontShow, setDontShow] = useState(false);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  if (!open || !LATEST_ID) return null;

  function close() {
    if (dontShow && LATEST_ID) writeSeen(LATEST_ID);
    setOpen(false);
  }

  // Esc zamyka <dialog> natywnie — przechwytujemy, żeby stan Reacta i checkbox się zgadzały.
  function handleCancel(event: SyntheticEvent<HTMLDialogElement>): void {
    event.preventDefault();
    close();
  }

  return (
    <dialog
      ref={dialogRef}
      className="whats-new"
      aria-labelledby="whats-new-title"
      onCancel={handleCancel}
    >
      <p className="whats-new__beta">
        <span className="whats-new__stamp hand">beta</span>
        <span>
          Statki są w fazie testów — mogą zdarzać się błędy. Coś nie działa? Napisz na{' '}
          <a className="whats-new__mail" href={`mailto:${CONTACT_EMAIL}`}>
            {CONTACT_EMAIL}
          </a>
          .
        </span>
      </p>

      <h2 id="whats-new-title" className="hand">
        Co nowego
      </h2>

      <div className="whats-new__list">
        {CHANGELOG.map((entry) => (
          <section key={entry.id} className="whats-new__entry">
            <h3 className="hand">{formatDate(entry.date)}</h3>
            <ul>
              {entry.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      <div className="whats-new__footer">
        <label className="whats-new__dont-show">
          <input
            type="checkbox"
            checked={dontShow}
            onChange={(event) => setDontShow(event.target.checked)}
          />
          Nie pokazuj do następnych zmian
        </label>
        <button type="button" className="btn btn--primary" onClick={close}>
          Zamknij
        </button>
      </div>
    </dialog>
  );
}
